import type { QueryClient } from '@tanstack/react-query';
import type { RFQ, RFQItem } from '../../../../types/rfq';
import type { EmailAttachment } from '../../../../types/email';
import type { ExcelPriceLine } from '../../../../services/excelAutomation';
import { queryKeys } from '../../../../api/queryKeys';

// The pure half of useQuoteExcel: what to send the automation, how to read
// its per-file answer, and how attachments are replaced. No I/O, no React.

const EXCEL_MIME_TYPE =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const XLS_MIME_TYPE = 'application/vnd.ms-excel';

// Fuji Trading's enquiries are BIFF .XLS and are filled and sent back as
// .XLS bytes, so the attachment must not claim to be xlsx.
export const excelMimeTypeFor = (filename: string) =>
  /\.xls$/i.test(filename) ? XLS_MIME_TYPE : EXCEL_MIME_TYPE;

/** One file's outcome, as `POST /excel/automation/process/:rfqId` reports it. */
export interface AutomationFileResult {
  filename: string;
  success?: boolean;
  updatedCount?: number;
  remarkUpdatedCount?: number;
  refused?: boolean;
  error?: string;
  contentBase64?: string;
  contentType?: string;
}

/** The RFQs a cached `['rfqs', ...]` entry holds: a plain list, or infinite-query pages. */
const rfqsIn = (data: unknown): RFQ[] => {
  if (Array.isArray(data)) return data as RFQ[];
  const pages = (data as { pages?: unknown[] } | null | undefined)?.pages;
  if (!Array.isArray(pages)) return [];
  return pages.flatMap((page) =>
    Array.isArray(page) ? (page as RFQ[]) : (((page as { data?: RFQ[] })?.data) ?? []),
  );
};

/**
 * This RFQ as the RFQ list's cache holds it, newest entry first.
 *
 * The list is cached under `['rfqs', activeTab, search]`, never `['rfqs']`
 * alone, so the exact-key `getQueryData(['rfqs'])` this replaced never found
 * anything and the Excel was always filled from the items captured when the
 * dialog rendered - without a remark typed just before opening it.
 */
export function findCachedRfq(queryClient: QueryClient, rfqId: number): RFQ | undefined {
  const queries = queryClient
    .getQueryCache()
    .findAll({ queryKey: queryKeys.rfqs.all })
    .sort((a, b) => b.state.dataUpdatedAt - a.state.dataUpdatedAt);
  for (const query of queries) {
    const found = rfqsIn(query.state.data).find((r) => r.rfq_id === rfqId);
    if (found) return found;
  }
  return undefined;
}

/**
 * The automation's input: every validated, priced item, in Excel row order
 * (order_index), with its remark tags, custom remark and - at price 0 - "Item
 * not available" joined into one supplier remark.
 */
export function buildPriceData(items: RFQItem[]): ExcelPriceLine[] {
  return items
    .slice() // Create a copy to avoid mutating the original array
    .sort((a, b) => (a.order_index ?? a.rfq_item_id) - (b.order_index ?? b.rfq_item_id))
    .filter(item => item.price_validated && item.quoted_price !== null && item.quoted_price !== undefined)
    .map((item, index) => {
      const remarks: string[] = [];
      const remarkTags = item.remark_tags;
      if (Array.isArray(remarkTags)) {
        remarks.push(...remarkTags);
      }
      if (item.remarks && item.remarks.trim()) {
        remarks.push(item.remarks.trim());
      }
      if (item.quoted_price === 0) {
        remarks.push('Item not available');
      }

      return {
        identifier: (index + 1).toString(),
        lineNumber: (index + 1).toString(),
        orderIndex: item.order_index,         // Maps to original Excel row position
        itemNumber: item.model_number,
        productName: item.item_name,
        price: item.quoted_price,
        supplierRemark: remarks.length > 0 ? remarks.join(', ') : null
      };
    });
}

export interface AutomationOutcome {
  /** Saved without a single price written: the pre-quote sheet in all but timestamp. */
  unfilled: AutomationFileResult[];
  /** Saved with prices in it. */
  written: AutomationFileResult[];
  /** Refused by the backend (e.g. not fillable by position). */
  refused: AutomationFileResult[];
  /** Every written file reported how many prices landed. */
  countKnown: boolean;
  /** Written files that took fewer prices than were sent. */
  partial: AutomationFileResult[];
  /** "N of M price(s) ... into file" for each written file. */
  writtenSummary: string;
}

/**
 * Read the automation's per-file results against the number of prices sent.
 *
 * A file the backend could not process is NOT updated: its copy in storage is
 * still the pre-quote sheet. Nor is a file it saved without writing a single
 * price - that happens when it cannot find the item rows (RFQ 63209 went to
 * Seven Seas that way with "Excel Files Updated" on screen), so it is handled
 * as a failure. What counts is what was written, not what was sent: the
 * backend sends every price and remark to each file and reports per file how
 * many landed in a row, so each file is judged against the full list.
 */
export function classifyAutomation(results: AutomationFileResult[], priceCount: number): AutomationOutcome {
  const isUnfilled = (r: AutomationFileResult) =>
    r.success === true && r.updatedCount === 0 && priceCount > 0;
  const unfilled = results.filter(isUnfilled);
  const written = results.filter((r) => r.success === true && !isUnfilled(r));
  const refused = results.filter((r) => r.refused === true);
  const countKnown = written.every((r) => typeof r.updatedCount === 'number');
  const partial = countKnown
    ? written.filter((r) => (r.updatedCount as number) < priceCount)
    : [];
  const writtenSummary = written
    .map((r) => `${r.updatedCount} of ${priceCount} price(s)${(r.remarkUpdatedCount ?? 0) > 0 ? ` and ${r.remarkUpdatedCount} supplier remark(s)` : ''} into ${r.filename}`)
    .join(', ');
  return { unfilled, written, refused, countKnown, partial, writtenSummary };
}

/** Replace the attachment of the same name, else add it at the end. */
export const upsertAttachment = (prev: EmailAttachment[], attachment: EmailAttachment): EmailAttachment[] => {
  const i = prev.findIndex(a => a.name === attachment.name);
  if (i >= 0) {
    const updated = [...prev];
    updated[i] = attachment;
    return updated;
  }
  return [...prev, attachment];
};

/** The toast for a stored Excel that could not be read back. */
export const readFailureToast = (
  failure: 'not_found' | 'no_path' | 'read_failed',
  readFailedTitle: string,
): { title: string; description: string } =>
  ({
    not_found: { title: "File Not Found", description: "Excel file not found in database" },
    no_path: { title: "File Not Available", description: "File not available in storage" },
    read_failed: { title: readFailedTitle, description: "Failed to download Excel file from storage" }
  })[failure];

/** A blob as bare base64 (the data URL's payload). */
export const blobToBase64 = (blob: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        const base64String = reader.result.split(',')[1] || reader.result;
        resolve(base64String);
      } else {
        reject(new Error('Failed to convert file to base64'));
      }
    };
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(blob);
  });
